import { makeAutoObservable } from "mobx";
import firebase from "firebase/compat/app";
import workspaces from "./workspaces";
import firebaseApp from "firebase/compat/app";
import { OKDocument, OKDocumentSource, OKFolder } from "../models/OrgKnowledge";
import { generateEmbedding, removeEmbedding, transferEmbedding } from "../services/chathub";
import { EmbeddingStatus } from "../models/HelpCenter";
import { v4 as uuid } from "uuid";

class OrganizationKnowledgeStore {
  private db: firebase.firestore.Firestore;
  folders: OKFolder[] = [];
  selectedFolder?: OKFolder;
  documents: OKDocument[] = [];

  constructor() {
    makeAutoObservable(this);

    this.db = firebase.firestore();
  }

  updateOrCreateFolder = async (folder: OKFolder) => {
    if (!workspaces.selectedWorkspace) {
      return;
    }

    if (folder?.id) {
      await this.db
        .collection("workspaces")
        .doc(String(workspaces.selectedWorkspace.id))
        .collection("orgKnowledgeFolders")
        .doc(folder.id)
        .set(
          {
            name: folder.name,
            updatedAt: firebaseApp.firestore.FieldValue.serverTimestamp(),
          },
          { merge: true }
        );

      const index = this.folders.findIndex((f) => f.id === folder.id);

      if (index > -1) {
        this.folders[index] = {
          ...this.folders[index],
          name: folder.name,
        };
      }

      if (this.selectedFolder?.id === folder.id) {
        this.selectFolder(folder);
      }
    } else {
      const folderObj: OKFolder = {
        name: folder.name,
        createdAt: firebaseApp.firestore.FieldValue.serverTimestamp(),
        updatedAt: firebaseApp.firestore.FieldValue.serverTimestamp(),
      };

      const docRef = await this.db
        .collection("workspaces")
        .doc(String(workspaces.selectedWorkspace.id))
        .collection("orgKnowledgeFolders")
        .add(folderObj);

      const newFolder = (await docRef.get()).data() as OKFolder;
      newFolder.id = docRef.id;
      this.folders.unshift(newFolder);
    }
  };

  deleteFolder = async (folder: OKFolder) => {
    if (!workspaces.selectedWorkspace) {
      return;
    }

    if (folder?.id) {
      await this.deleteAllDocumentsOfAFolder(folder.id);

      await this.db
        .collection("workspaces")
        .doc(String(workspaces.selectedWorkspace.id))
        .collection("orgKnowledgeFolders")
        .doc(folder.id)
        .delete();

      const index = this.folders.findIndex((f) => f.id === folder.id);

      if (index > -1) {
        this.folders.splice(index, 1);
      }
    }
  };

  loadFolders = async () => {
    if (!workspaces.selectedWorkspace) {
      return;
    }

    const snapshot = await this.db
      .collection("workspaces")
      .doc(String(workspaces.selectedWorkspace.id))
      .collection("orgKnowledgeFolders")
      .orderBy("createdAt", "desc")
      .get();

    const folders = snapshot.docs.map((doc) => {
      const obj = doc.data() as OKFolder;
      obj.id = doc.id;
      return obj;
    }) as OKFolder[];

    this.folders = folders;
  };

  selectFolder = (folder: OKFolder) => {
    this.selectedFolder = folder;
  };

  updateOrCreateDocument = async (document: OKDocument) => {
    if (!workspaces.selectedWorkspace || !this.selectedFolder) {
      return;
    }

    if (document?.id) {
      if (
        document.content instanceof File &&
        document.source === OKDocumentSource.UPLOAD
      ) {
        const ref = firebase.storage().ref(`files`);
        const fileName = document.content.name;
        const fileExtension = fileName.substring(fileName.lastIndexOf(".") + 1);
        const fileRef = ref.child(`${uuid()}.${fileExtension}`);
        await fileRef.put(document.content, {
          contentType: (document.content as File).type,
        });
        document.content = await fileRef.getDownloadURL();
      }

      await this.db
        .collection("workspaces")
        .doc(String(workspaces.selectedWorkspace.id))
        .collection("orgKnowledgeDocuments")
        .doc(document.id)
        .set(
          {
            folderId: this.selectedFolder.id,
            name: document.name,
            content: document.content,
            source: document.source,
            embeddingStatus: EmbeddingStatus.PENDING,
            updatedAt: firebaseApp.firestore.FieldValue.serverTimestamp(),
          },
          { merge: true }
        );

      const index = this.documents.findIndex((f) => f.id === document.id);

      const updatedDocument = {
        ...this.documents[index],
        ...document,
        embeddingStatus: EmbeddingStatus.PENDING,
      };

      if (index > -1) {
        this.documents[index] = updatedDocument;
      }

      await generateEmbedding(workspaces.selectedWorkspace.id, {
        collectionId: document.id,
        collectionName: `org-knowledge-${this.selectedFolder.id}`,
        meta: {
          source: document.source,
          content: document.content,
          name: document.name,
        },
        text: String(updatedDocument.content ?? ""),
      });
    } else {
      if (document.source === OKDocumentSource.UPLOAD) {
        const ref = firebase.storage().ref(`files`);
        const fileName = document.content.name;
        const fileExtension = fileName.substring(fileName.lastIndexOf(".") + 1);
        const fileRef = ref.child(`${uuid()}.${fileExtension}`);
        await fileRef.put(document.content, {
          contentType: (document.content as File).type,
        });
        document.content = await fileRef.getDownloadURL();
      }

      const documentObj: OKDocument = {
        ...document,
        folderId: this.selectedFolder?.id || "",
        embeddingStatus: EmbeddingStatus.PENDING,
        createdAt: firebaseApp.firestore.FieldValue.serverTimestamp(),
        updatedAt: firebaseApp.firestore.FieldValue.serverTimestamp(),
      };

      const docRef = await this.db
        .collection("workspaces")
        .doc(String(workspaces.selectedWorkspace.id))
        .collection("orgKnowledgeDocuments")
        .add(documentObj);

      await this.updateFolderCount(1);

      const newDocument = (await docRef.get()).data() as OKDocument;
      newDocument.id = docRef.id;
      this.documents.unshift(newDocument);

      await generateEmbedding(workspaces.selectedWorkspace.id, {
        collectionId: newDocument.id,
        collectionName: `org-knowledge-${this.selectedFolder.id}`,
        meta: {
          source: newDocument.source,
          content: newDocument.content,
          name: newDocument.name,
        },
        text: String(newDocument.content ?? ""),
      });
    }
  };

  loadDocuments = async () => {
    if (!workspaces.selectedWorkspace || !this.selectedFolder) {
      return;
    }

    const snapshot = await this.db
      .collection("workspaces")
      .doc(String(workspaces.selectedWorkspace.id))
      .collection("orgKnowledgeDocuments")
      .where("folderId", "==", this.selectedFolder.id)
      .orderBy("createdAt", "desc")
      .get();

    const documents = snapshot.docs.map((doc) => {
      const obj = doc.data() as OKDocument;
      obj.id = doc.id;
      return obj;
    }) as OKDocument[];

    this.documents = documents;
  };

  deleteDocument = async (
    document: OKDocument,
    dontUpdateFolderCount = false
  ) => {
    if (!workspaces.selectedWorkspace || !this.selectedFolder) {
      return;
    }
    if (document?.id) {
      await this.db
        .collection("workspaces")
        .doc(String(workspaces.selectedWorkspace.id))
        .collection("orgKnowledgeDocuments")
        .doc(document.id)
        .delete();

      await removeEmbedding(workspaces.selectedWorkspace.id, {
        collectionId: document.id,
        collectionName: `org-knowledge-${this.selectedFolder.id}`,
      });

      const index = this.documents.findIndex((f) => f.id === document.id);

      if (index > -1) {
        this.documents.splice(index, 1);
      }

      if (!dontUpdateFolderCount) {
        await this.updateFolderCount(-1);
      }
    }
  };

  updateFolderCount = async (amount: number) => {
    if (!workspaces.selectedWorkspace || !this.selectedFolder?.id) return;

    const documentsCount = (this.selectedFolder?.documentsCount || 0) + amount;

    await this.db
      .collection("workspaces")
      .doc(String(workspaces.selectedWorkspace.id))
      .collection("orgKnowledgeFolders")
      .doc(this.selectedFolder.id)
      .set(
        {
          documentsCount,
          updatedAt: firebaseApp.firestore.FieldValue.serverTimestamp(),
        },
        { merge: true }
      );

    const index = this.folders.findIndex(
      (f) => f.id === this.selectedFolder?.id
    );

    if (index > -1) {
      this.folders[index] = {
        ...this.folders[index],
        documentsCount,
      };
    }

    this.selectedFolder.documentsCount = documentsCount;
  };

  deleteAllDocumentsOfAFolder = async (folderId: string) => {
    if (!workspaces.selectedWorkspace) return;

    const snapshot = await this.db
      .collection("workspaces")
      .doc(String(workspaces.selectedWorkspace.id))
      .collection("orgKnowledgeDocuments")
      .where("folderId", "==", folderId)
      .get();

    for (let map of snapshot.docs) {
      const document = map.data() as OKDocument;
      document.id = map.id;
      await this.deleteDocument(document, true);
    }
  };

  transferDocuments = async (
    documentIds: string[],
    targetWorkspaceId: string,
    targetFolderId: string
  ) => {
    if (!workspaces.selectedWorkspace || !this.selectedFolder) {
      throw new Error("No workspace or folder selected");
    }

    const sourceWorkspaceId = workspaces.selectedWorkspace.id;
    const sourceFolderId = this.selectedFolder.id;

    if (sourceWorkspaceId === targetWorkspaceId) {
      throw new Error("Cannot transfer to the same workspace");
    }

    try {
      // Get all documents to transfer
      const documentsToTransfer = this.documents.filter((doc) =>
        documentIds.includes(doc.id!)
      );

      if (documentsToTransfer.length === 0) {
        throw new Error("No documents found to transfer");
      }

      // Prepare embedding transfer data
      const embeddingTransferData = documentsToTransfer.map((doc) => ({
        sourceCollectionId: doc.id!,
        sourceCollectionName: `org-knowledge-${sourceFolderId}`,
        targetCollectionId: doc.id!,
        targetCollectionName: `org-knowledge-${targetFolderId}`,
        text: String(doc.content ?? ""),
        meta: {
          source: doc.source,
          content: doc.content,
          name: doc.name,
        },
      }));

      // Transfer embeddings in the vector database
      await transferEmbedding(
        sourceWorkspaceId,
        targetWorkspaceId,
        embeddingTransferData
      );

      // Create documents in target workspace
      const batch = this.db.batch();

      for (const doc of documentsToTransfer) {
        const newDocRef = this.db
          .collection("workspaces")
          .doc(targetWorkspaceId)
          .collection("orgKnowledgeDocuments")
          .doc(doc.id);

        batch.set(newDocRef, {
          folderId: targetFolderId,
          name: doc.name,
          content: doc.content,
          source: doc.source,
          embeddingStatus: EmbeddingStatus.TRAINED,
          lastEmbeddidAt: firebaseApp.firestore.FieldValue.serverTimestamp(),
          createdAt: firebaseApp.firestore.FieldValue.serverTimestamp(),
          updatedAt: firebaseApp.firestore.FieldValue.serverTimestamp(),
        });
      }

      await batch.commit();

      // Delete documents from source workspace
      for (const doc of documentsToTransfer) {
        await this.db
          .collection("workspaces")
          .doc(sourceWorkspaceId)
          .collection("orgKnowledgeDocuments")
          .doc(doc.id!)
          .delete();

        // Remove from local state
        const index = this.documents.findIndex((d) => d.id === doc.id);
        if (index > -1) {
          this.documents.splice(index, 1);
        }
      }

      // Update source folder count
      await this.updateFolderCount(-documentsToTransfer.length);

      // Update target folder count
      const targetFolderRef = this.db
        .collection("workspaces")
        .doc(targetWorkspaceId)
        .collection("orgKnowledgeFolders")
        .doc(targetFolderId);

      const targetFolderSnap = await targetFolderRef.get();
      const targetFolder = targetFolderSnap.data() as OKFolder;
      const newCount = (targetFolder?.documentsCount || 0) + documentsToTransfer.length;

      await targetFolderRef.set(
        {
          documentsCount: newCount,
          updatedAt: firebaseApp.firestore.FieldValue.serverTimestamp(),
        },
        { merge: true }
      );

      return {
        success: true,
        transferred: documentsToTransfer.length,
      };
    } catch (error) {
      console.error("Error transferring documents:", error);
      throw error;
    }
  };

  clearChatAndMessages = () => {
    this.documents = [];
    this.folders = [];
    this.selectedFolder = undefined;
  };
}

export default new OrganizationKnowledgeStore();
